/**
 * AutoFixCoverage.tsx — Admin audit page for auto-fix executor coverage.
 *
 * Shows which Tier-1 check_ids have executor functions and which are gaps.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE from '../../config/api';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// ── Types ────────────────────────────────────────────────────────────────────

interface CoverageCheck {
  check_id: string;
  provider: string;
  tier: string;
  has_executor: boolean;
  gap: boolean;
}

interface CoverageReport {
  checks: CoverageCheck[];
  total_tier1: number;
  covered: number;
  gaps_count: number;
  last_checked: string | null;
}

// ── Constants ────────────────────────────────────────────────────────────────

const PROVIDER_LABELS: Record<string, string> = {
  aws: 'AWS', gcp: 'GCP', azure: 'Azure', ssh: 'SSH',
};

const PROVIDER_COLORS: Record<string, string> = {
  aws: '#f77f00', gcp: '#2dc653', azure: '#00b4d8', ssh: '#9c6ade',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ── Main component ──────────────────────────────────────────────────────────

export default function AutoFixCoverage() {
  const [report, setReport]     = useState<CoverageReport | null>(null);
  const [loading, setLoading]   = useState(true);
  const [syncing, setSyncing]   = useState(false);
  const [error, setError]       = useState('');
  const [provFilter, setProvFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');

  const load = () => {
    setLoading(true);
    setError('');
    axios.get<CoverageReport>(`${API_BASE}/admin/autofix-coverage`)
      .then(r => setReport(r.data))
      .catch(() => setError('Failed to load coverage data.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const sync = () => {
    setSyncing(true);
    axios.post<CoverageReport>(`${API_BASE}/admin/autofix-coverage/sync`)
      .then(r => setReport(r.data))
      .catch(() => setError('Sync failed.'))
      .finally(() => setSyncing(false));
  };

  if (loading) return <Box p={4}><CircularProgress /></Box>;
  if (error && !report) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;

  const checks = report?.checks ?? [];
  const filtered = checks
    .filter(c => provFilter === 'all' || c.provider === provFilter)
    .filter(c => {
      if (tierFilter === 'all') return true;
      if (tierFilter === 'gaps') return c.gap;
      return c.tier === tierFilter;
    });

  const gaps = checks.filter(c => c.gap);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoFixHighIcon color="primary" /> Auto-Fix Coverage Audit
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Tier-1 checks without executor functions will fail when approved
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {report?.last_checked && (
            <Typography variant="caption" color="text.disabled">
              Last checked: {formatDate(report.last_checked)}
            </Typography>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={sync}
            disabled={syncing}
          >
            Sync Coverage
          </Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Gap alert */}
      {gaps.length > 0 && (
        <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
          <strong>{gaps.length} auto-fix check{gaps.length > 1 ? 's have' : ' has'} no executor</strong> and will fail when approved.
          {' '}Findings matching these check IDs are automatically downgraded to Tier-2 guided instructions.
        </Alert>
      )}

      {/* Summary bar */}
      {report && (
        <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" alignItems="center">
          <Chip
            label={`${report.covered}/${report.total_tier1} checks covered`}
            color={report.gaps_count === 0 ? 'success' : 'warning'}
            sx={{ fontWeight: 700 }}
          />
          {report.gaps_count > 0 && (
            <Chip label={`${report.gaps_count} gap${report.gaps_count > 1 ? 's' : ''}`} color="error" sx={{ fontWeight: 700 }} />
          )}
          {report.gaps_count === 0 && (
            <Chip label="Full coverage" color="success" variant="outlined" sx={{ fontWeight: 700 }} />
          )}
        </Stack>
      )}

      {/* Filters */}
      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Provider</InputLabel>
          <Select value={provFilter} label="Provider" onChange={e => setProvFilter(e.target.value)}>
            <MenuItem value="all">All Providers</MenuItem>
            <MenuItem value="aws">AWS</MenuItem>
            <MenuItem value="gcp">GCP</MenuItem>
            <MenuItem value="azure">Azure</MenuItem>
            <MenuItem value="ssh">SSH</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Tier</InputLabel>
          <Select value={tierFilter} label="Tier" onChange={e => setTierFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="automatic">Tier-1 (Auto)</MenuItem>
            <MenuItem value="guided">Tier-2 (Guided)</MenuItem>
            <MenuItem value="gaps">Gaps only</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: 'background.default' } }}>
              <TableCell>Provider</TableCell>
              <TableCell>Check ID</TableCell>
              <TableCell>Classifier Tier</TableCell>
              <TableCell>Executor</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(c => (
              <TableRow
                key={`${c.provider}-${c.check_id}`}
                sx={c.gap ? { bgcolor: 'rgba(239, 35, 60, 0.04)' } : undefined}
              >
                <TableCell>
                  <Chip
                    label={PROVIDER_LABELS[c.provider] ?? c.provider.toUpperCase()}
                    size="small"
                    sx={{ fontWeight: 700, bgcolor: PROVIDER_COLORS[c.provider] ?? '#555', color: '#000', fontSize: '0.7rem' }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {c.check_id}
                  </Typography>
                </TableCell>
                <TableCell>
                  {c.tier === 'automatic' ? (
                    <Chip label="Tier-1 Auto" color="success" size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
                  ) : (
                    <Chip label="Tier-2 Guided" color="info" size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
                  )}
                </TableCell>
                <TableCell>
                  {c.tier === 'automatic' ? (
                    c.has_executor
                      ? <Typography variant="body2" color="success.main" fontWeight={600}>Yes</Typography>
                      : <Typography variant="body2" color="error.main" fontWeight={600}>Missing</Typography>
                  ) : (
                    <Typography variant="caption" color="text.disabled">N/A</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {c.gap ? (
                    <Chip label="❌ Gap — will fail" color="error" size="small" sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
                  ) : c.tier === 'automatic' && c.has_executor ? (
                    <Chip label="✅ Covered" color="success" size="small" sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
                  ) : (
                    <Chip label="⚠️ Tier-2 only" color="warning" size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.disabled" py={3}>
                    No checks match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
